import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Plus, Trash, MagicWand, ShieldCheck, Target } from "@phosphor-icons/react";
import { Section, Pill, EmptyState, severityTone, SEVERITY_HEX } from "./_shared";
import { AIEnrichButton, AIBulkGenerateModal, AIAssistant } from "./_ai";

const SEV_COLOR = (impact, likelihood) => {
  const score = impact * likelihood;
  if (score >= 16) return SEVERITY_HEX.critical;
  if (score >= 9)  return SEVERITY_HEX.high;
  if (score >= 4)  return SEVERITY_HEX.medium;
  return SEVERITY_HEX.low;
};

const Heatmap = ({ risks }) => {
  const cells = [];
  for (let imp = 5; imp >= 1; imp--) {
    for (let lik = 1; lik <= 5; lik++) {
      const inCell = risks.filter(r => r.impact === imp && r.likelihood === lik);
      cells.push({ imp, lik, count: inCell.length, color: SEV_COLOR(imp, lik) });
    }
  }
  return (
    <div className="grid grid-cols-[auto_repeat(5,minmax(0,1fr))] gap-px bg-zinc-200 border border-zinc-200">
      <div />
      {[1, 2, 3, 4, 5].map((l) => (
        <div key={`h${l}`} className="bg-white text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 text-center py-2">L{l}</div>
      ))}
      {cells.map((c) => {
        const showLabel = c.lik === 1;
        const cell = (
          <div key={`cell-${c.imp}-${c.lik}`} className="relative bg-white aspect-square flex items-center justify-center" data-testid={`heatmap-cell-${c.imp}-${c.lik}`}>
            <div className="absolute inset-2" style={{ background: c.count > 0 ? c.color : "#F4F4F5", opacity: c.count > 0 ? 0.9 : 1 }} />
            {c.count > 0 && (
              <span className="relative font-display font-black text-xl text-white" data-testid={`heatmap-count-${c.imp}-${c.lik}`}>{c.count}</span>
            )}
          </div>
        );
        if (showLabel) {
          return (
            <React.Fragment key={`row-${c.imp}-${c.lik}`}>
              <div className="bg-white text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center justify-center px-3">I{c.imp}</div>
              {cell}
            </React.Fragment>
          );
        }
        return cell;
      })}
    </div>
  );
};

const LMHPill = ({ value }) => {
  if (!value) return null;
  const tone = value === "H" ? "red" : value === "M" ? "orange" : "green";
  return <Pill tone={tone}>{value}</Pill>;
};

export default function RiskLens({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", impact: 3, likelihood: 3 });
  const [creating, setCreating] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [mitigatingId, setMitigatingId] = useState(null);
  const [minimizingId, setMinimizingId] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditCount, setAuditCount] = useState(6);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/risks`);
      setItems(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks`, form);
      setItems((p) => [data, ...p]);
      setForm({ title: "", description: "", impact: 3, likelihood: 3 });
    } catch { toast.error("Failed to add"); }
    finally { setCreating(false); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/audit`, { count: auditCount });
      toast.success(`AI generated ${data.length} risks from project context`);
      setItems((p) => [...data, ...p]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setAuditing(false); }
  };

  const analyze = async (id) => {
    setAnalyzingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/${id}/analyze`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Risk analysed");
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzingId(null); }
  };

  const mitigation = async (id) => {
    setMitigatingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/${id}/mitigation-strategy`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Mitigation strategy generated");
    } catch { toast.error("Failed"); }
    finally { setMitigatingId(null); }
  };

  const minimize = async (id) => {
    setMinimizingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/${id}/minimize`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Risk minimization plan ready");
    } catch { toast.error("Failed"); }
    finally { setMinimizingId(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/projects/${projectId}/risks/${id}`); setItems(p => p.filter(x => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  const stats = useMemo(() => {
    const sev = { critical: 0, high: 0, medium: 0, low: 0 };
    items.forEach(r => { if (r.severity && sev[r.severity] !== undefined) sev[r.severity]++; });
    return sev;
  }, [items]);

  return (
    <div data-testid="risk-lens">
      <div className="grid sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-6">
        <div className="bg-white p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Total risks</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="risk-total">{items.length}</div>
        </div>
        <div className="bg-white p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Critical</div>
          <div className="font-display font-black text-3xl mt-1" style={{ color: "#FF3B30" }} data-testid="risk-critical">{stats.critical}</div>
        </div>
        <div className="bg-white p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">High</div>
          <div className="font-display font-black text-3xl mt-1" style={{ color: "#FF9500" }} data-testid="risk-high">{stats.high}</div>
        </div>
        <div className="bg-white p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Medium / Low</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="risk-med-low">{stats.medium + stats.low}</div>
        </div>
      </div>

      <Section
        eyebrow="ML 1.1 · Audit"
        title="AI Risk Audit"
        action={
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-600">Count</label>
            <input type="number" min="3" max="12" value={auditCount}
              onChange={(e) => setAuditCount(Number(e.target.value))}
              className="w-16 h-10 px-2 border border-zinc-300 text-sm text-center"
              data-testid="audit-count-input" />
            <button onClick={runAudit} disabled={auditing}
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#FF3B30] text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
              data-testid="run-risk-audit-button">
              <MagicWand size={14} weight="fill" />
              {auditing ? "Auditing…" : "Run risk audit"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-zinc-600">ARISSAI AI generates a PMP-aligned risk register from your project scope, schedule, budget, resources, and vendor context. Each risk gets Probability (L/M/H), Impact (L/M/H), and a suggested mitigation.</p>
      </Section>

      <Section eyebrow="Forecast" title="Risk heatmap" >
        <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-start">
          <Heatmap risks={items} />
          <div className="flex flex-col gap-3 lg:w-56">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Severity</div>
            <div className="flex items-center gap-2 text-xs"><span className="w-4 h-4" style={{ background: SEVERITY_HEX.critical }} /> Critical (I×L ≥ 16)</div>
            <div className="flex items-center gap-2 text-xs"><span className="w-4 h-4" style={{ background: SEVERITY_HEX.high }} /> High (9–15)</div>
            <div className="flex items-center gap-2 text-xs"><span className="w-4 h-4" style={{ background: SEVERITY_HEX.medium }} /> Medium (4–8)</div>
            <div className="flex items-center gap-2 text-xs"><span className="w-4 h-4" style={{ background: SEVERITY_HEX.low }} /> Low (1–3)</div>
            <div className="mt-4 text-xs text-zinc-500">Rows = Impact (1–5), Columns = Likelihood (1–5). Numbers indicate count per cell.</div>
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Add"
        title="New risk (manual)"
        action={
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold hover:bg-[#0020F5]"
            data-testid="bulk-gen-risk-button"
          >
            <MagicWand size={14} weight="fill" /> AI bulk generate
          </button>
        }
      >
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Risk title"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="risk-title-input" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Short description (optional)"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="risk-desc-input" />
          <label className="flex items-center gap-3 text-sm">
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600 w-20">Impact</span>
            <input type="range" min="1" max="5" value={form.impact}
              onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })}
              className="flex-1" data-testid="risk-impact-slider" />
            <span className="font-mono w-6 text-right">{form.impact}</span>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600 w-20">Likelihood</span>
            <input type="range" min="1" max="5" value={form.likelihood}
              onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })}
              className="flex-1" data-testid="risk-likelihood-slider" />
            <span className="font-mono w-6 text-right">{form.likelihood}</span>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <AIEnrichButton
              projectId={projectId}
              lens="risk"
              partial={{ title: form.title, description: form.description }}
              onEnriched={(enriched) => setForm((p) => ({
                ...p,
                title: enriched.title || p.title,
                description: enriched.description || p.description,
                impact: enriched.impact || p.impact,
                likelihood: enriched.likelihood || p.likelihood,
              }))}
            />
            <button type="submit" disabled={creating}
              className="h-12 px-6 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="add-risk-button">
              <Plus size={16} weight="bold" /> {creating ? "Adding..." : "Add risk"}
            </button>
          </div>
        </form>
      </Section>

      <Section eyebrow="Register" title={`Risks (${items.length})`}>
        {loading ? <div className="text-zinc-500 text-sm">Loading…</div> :
          items.length === 0 ? <EmptyState title="No risks logged" hint="Run the AI Risk Audit above, or add manually." testid="empty-risks" /> :
          <div className="divide-y divide-zinc-200 border border-zinc-200">
            {items.map(r => (
              <div key={r.id} className="p-5" data-testid={`risk-item-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display font-bold text-lg">{r.title}</h3>
                      {r.source === "audit" && <Pill tone="black">AI Audit</Pill>}
                      {r.severity && <Pill tone={severityTone(r.severity)}>{r.severity}</Pill>}
                      <Pill>I{r.impact} · L{r.likelihood}</Pill>
                      {r.probability_level && <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">P:<LMHPill value={r.probability_level} /></span>}
                      {r.impact_level && <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">I:<LMHPill value={r.impact_level} /></span>}
                      {r.mitigation_priority != null && <Pill tone="black">Priority {r.mitigation_priority}/10</Pill>}
                    </div>
                    {r.description && <p className="text-sm text-zinc-700 mt-2">{r.description}</p>}

                    {(r.suggested_mitigation || r.forecast || r.ai_summary) && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        {r.suggested_mitigation && (
                          <div className="bg-zinc-50 border border-zinc-200 p-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Suggested mitigation</div>
                            <p className="text-sm text-zinc-800 mt-1">{r.suggested_mitigation}</p>
                          </div>
                        )}
                        {r.forecast && (
                          <div className="bg-zinc-50 border border-zinc-200 p-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">AI forecast</div>
                            <p className="text-sm text-zinc-800 mt-1">{r.forecast}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {(r.preventive_action || r.contingency_plan || r.responsible_owner) && (
                      <div className="mt-3 grid sm:grid-cols-3 gap-3 border-l-4 border-[#0020F5] pl-4">
                        {r.preventive_action && (
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">Preventive action</div>
                            <p className="text-sm text-zinc-800 mt-1">{r.preventive_action}</p>
                          </div>
                        )}
                        {r.contingency_plan && (
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">Contingency plan</div>
                            <p className="text-sm text-zinc-800 mt-1">{r.contingency_plan}</p>
                          </div>
                        )}
                        {r.responsible_owner && (
                          <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">Responsible owner</div>
                            <p className="text-sm text-zinc-800 mt-1">{r.responsible_owner}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {(r.monitoring_method || r.expected_probability_reduction || (r.early_warning_indicators?.length > 0)) && (
                      <div className="mt-3 border-l-4 border-[#00C7BE] pl-4">
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00C7BE]">Minimize occurrence</div>
                        {r.monitoring_method && <p className="text-sm text-zinc-800 mt-1"><span className="font-semibold">Monitor:</span> {r.monitoring_method}</p>}
                        {r.early_warning_indicators?.length > 0 && (
                          <div className="mt-1">
                            <span className="text-xs font-semibold text-zinc-700">Early warning:</span>
                            <ul className="text-xs text-zinc-700 list-disc list-inside">{r.early_warning_indicators.map((ind, k) => <li key={`ew-${k}-${(ind || "").slice(0, 30)}`}>{ind}</li>)}</ul>
                          </div>
                        )}
                        {r.expected_probability_reduction && <p className="text-xs text-zinc-700 mt-1"><span className="font-semibold">Expected reduction:</span> {r.expected_probability_reduction}</p>}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0 w-44">
                    <button onClick={() => analyze(r.id)} disabled={analyzingId === r.id}
                      className="inline-flex items-center gap-2 h-9 px-3 bg-[#FF3B30] text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
                      data-testid={`analyze-risk-${r.id}`}>
                      <Sparkle size={12} weight="fill" />
                      {analyzingId === r.id ? "Analysing…" : "Analyse forecast"}
                    </button>
                    <button onClick={() => mitigation(r.id)} disabled={mitigatingId === r.id}
                      className="inline-flex items-center gap-2 h-9 px-3 bg-[#0020F5] text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                      data-testid={`mitigation-risk-${r.id}`}>
                      <ShieldCheck size={12} weight="fill" />
                      {mitigatingId === r.id ? "Working…" : "Mitigation plan"}
                    </button>
                    <button onClick={() => minimize(r.id)} disabled={minimizingId === r.id}
                      className="inline-flex items-center gap-2 h-9 px-3 bg-[#00C7BE] text-white text-xs font-bold hover:bg-teal-700 disabled:opacity-50"
                      data-testid={`minimize-risk-${r.id}`}>
                      <Target size={12} weight="fill" />
                      {minimizingId === r.id ? "Working…" : "Minimize"}
                    </button>
                    <button onClick={() => remove(r.id)} className="inline-flex items-center justify-center h-9 px-3 border border-zinc-300 text-zinc-700 hover:text-[#FF3B30] hover:border-[#FF3B30] text-xs" data-testid={`delete-risk-${r.id}`}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      </Section>
      <AIBulkGenerateModal
        projectId={projectId}
        lens="risk"
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => load()}
      />
      <AIAssistant projectId={projectId} lens="risk" label="Risk" />
    </div>
  );
}
